import React, { useState, useEffect, useRef, useMemo } from 'react';
import UploadExcelFile from './UploadExcelFile.jsx';
import ReviewValues from './ReviewValues.jsx';

import {validationDisplayCount, COLUMN_ERROR_LIMIT} from './helpers.js';
import ImportConfigModification from './ImportConfigModification.jsx';
import ImportConfigSelection from './ImportConfigSelection.jsx';

import ColumnImportConfig from './ColumnImportConfig.jsx';
import {stepFullyConfigured} from './helpers.js'
import usePersistentConfig from './usePersistentConfig.js';

// Used to exclude system columns from processing
const DATA_TYPES_SYSTEM_PROVIDED = ['project', 'source', 'recorded_at', 'last_modified_at', 'last_modified_by'];

export const DEFAULT_XLSX_WORK_BOOK = {
    file: null,
    wb: {},
    ws: {},
    ws_column_names: [],
    ws_names: [],
    total_row_count: 0,
    resolved_column_types: []
  }

export default function useSteps(
  gridRef,
  datagrid,
  settings,
  updateSettings,
  xlsSettings = DEFAULT_XLSX_WORK_BOOK,
  fileSizeError,
  setFileSizeError,
  markReadyForExport,
  context,
  toggleGrid,
  wizardOptions = {
    showBodyRowSelector: true,
    enableControls: false,
  }
){
  const gridApi = gridRef?.current?.api;
  const columns = datagrid.columns.filter((c) => !DATA_TYPES_SYSTEM_PROVIDED.includes(c.data_type));

  const [validationErrors, setValidationErrors] = useState([]);
  const [enableControls, setEnableControls] = useState(wizardOptions.enableControls);
  const [processingExcel, setProcessingExcel] = useState(false);
  const [xlsWorkBook, setXlsWorkBook] = useState(xlsSettings);

  const validateColumns = (colDefs, rowIterationLimit = 300000) => {
        let countsByCol = {};
        let startTime = Date.now();
        let rowsProcessed = 0;
        let rowsValidated = 0;
        // console.warn("Validate:", colDefs, rangeFrom, rangeTo)
    
        gridRef.current.api.forEachNode((rowNode, index) => {
          if (rowsValidated > rowIterationLimit) return;
          rowsProcessed++;
          if (!rowNode.displayed) return;
          // if (rangeTo < rowsProcessed) return;
          // if (rangeFrom > rowsProcessed) return;
          rowsValidated++;     

          colDefs.forEach(colDef  => {
            countsByCol[colDef.field] ||= [];
            if (countsByCol[colDef.field]?.length > COLUMN_ERROR_LIMIT) return;
    
            const validationRule = colDef.cellClassRules?.['import-cell-error'];
            const blankRule = colDef.cellClassRules?.['import-cell-blank'];

            if(typeof validationRule !== 'function' && typeof blankRule !== 'function') return;
    
            const value = colDef.valueGetter({data: rowNode.data});
            if (typeof blankRule === 'function' && blankRule({value})) {
              countsByCol[colDef.field].push(rowNode.id);
            }
            else if (typeof validationRule === 'function' && validationRule({ value })) {
              countsByCol[colDef.field].push(rowNode.id);
            }
          });
        });
    
        console.info(`Row validation processed ${rowsProcessed} rows. Validated ${rowsValidated} rows. Took: ${Date.now() - startTime}ms`);
    
        setValidationErrors(validationErrors => ({...validationErrors, ...countsByCol}))
    
        return Object.keys(countsByCol).some(accessor => countsByCol[accessor].length > 0)
      }

  // Ensure the colDefs have been loaded.
  const validateAllColumns = () => {
      const accessors = columns.map(c => c.accessor)
      validateColumns(gridApi.getGridOption('columnDefs').filter(c =>  accessors.includes(c.field)));
  }

  const validateColumn = (column) => {
    let colDef = gridApi.getGridOption('columnDefs').filter(c => c.field == column.accessor)
    validateColumns(colDef)
  };

  const {
      allConfigs, 
      setAllConfigs,
      selectedColConfig, 
      setSelectedColConfig,      
  } = usePersistentConfig(settings, updateSettings, context, gridApi, validateAllColumns, columns);

  // Used to check if steps are completed (or even if they need to be completed)
  const isStepCompleted = (range_from, col_accessor) => {
    let stepSettings = settings.columns[col_accessor];
    let isFailed = validationErrors?.[col_accessor]?.length > 0;

    let isCompleted = stepFullyConfigured(stepSettings) && !isFailed;

    let highlightError = isFailed && stepFullyConfigured; // Not sure when would this happen

    return {completed: isCompleted, error: (isFailed && highlightError)}
  };


  const updateColumnImportConfig = (accessor, newValue) => {
    updateSettings({columns: {...settings.columns, [accessor]: newValue}});
  }

  let steps = [];
    steps.push({
      label: I18n.t('upload_excel_file'), 
      enableControls: !processingExcel && enableControls,
      content: <UploadExcelFile {...{
        gridRef, datagrid, xlsWorkBook, setXlsWorkBook, settings, updateSettings, processingExcel, setProcessingExcel, setEnableControls, setFileSizeError, toggleGrid, fileSizeError, wizardOptions
      }} />,
      error: fileSizeError
    })

    // Config loading
    if (context.permissions['view_import_config']){
      steps.push(
        {
          label: I18n.t('datagrid_config_import_select_title'), 
          key: 'config_select',
          enableControls: true,
          content: <ImportConfigSelection key='config-selection' {...{allConfigs, selectedColConfig, setSelectedColConfig}} />
        }
      )
    }

    const stepsBeforeColumns = steps.length;

    // Add datagrid columns
    columns.forEach((column) => {

      let count = validationDisplayCount(validationErrors[column.accessor]);
      let stepStatus = isStepCompleted(settings?.range_from, column.accessor);
      const stepValidationMsg = !!count && <div className='description mt-0'>{I18n.t('import_validation_errors', {count: count})}</div>
      const stepMsg = stepValidationMsg || <div className='description mt-0'>{stepStatus?.completed ? I18n.t('done') : I18n.t('configure_column')}</div>
      steps.push({ 
        label: column.name, 
        dataType: I18n.t(`data_type_${column.column_type}`),
        validationErrors: validationErrors[column.accessor],
        key: column.accessor,
        enableControls: true,
        labelDescription: stepMsg,
        ...stepStatus,
        content: <ColumnImportConfig 
          {...{column,
            xlsWorkBook,
            settings,
            updateColumnImportConfig,
            gridRef,
            toggleGrid,
            validate: validateColumn}} 
        />
      })
    });

    const stepsAfterColumns = steps.length;


    if (context.permissions['manage_import_config']) {
      // Config saving
      steps.push({
        label: I18n.t('datagrid_config_save_title'), 
        key: 'config_save', props: {completed: true, error: false},
        content: <ImportConfigModification 
          key='config-modification' 
          configuration={settings.columns}  
          {...{selectedColConfig, context, allConfigs, setAllConfigs, setSelectedColConfig}} 
        />,
        enableControls: true
      })
    }

    // Final review
    steps.push({
      label: I18n.t('review_values'), 
      content: <ReviewValues {...{datagrid, gridRef, xlsWorkBook, settings}} />,
      enableControls: true
    })

    const validateStep = (step) => {
      // NOP
      // if(step <= stepsBeforeColumns) return true;
      // if(step > stepsAfterColumns) return true;

      // const currentColumn = columns[step - stepsBeforeColumns - 1];
      // validate(gridApi.getGridOption('columnDefs').filter(c => c.field == currentColumn.accessor));
    }

    return {steps, validateStep, xlsWorkBook: xlsWorkBook};
}